import { FC, useMemo } from 'react'
import { DeleteOutlined } from '@ant-design/icons'
import { chatApi } from '@/api'
import { useApiSearch } from '@/hooks/useApiSearch'
import { compareOptionsByLabel } from '@/helpers/new/compareOptionsByLabel'
import Select from '@UI/New/Select'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import classes from './index.module.css'

export type ChatRole = 1 | 2 | 3

export type ChatAudience = 'student' | 'parent' | 'staff'

export interface ChatOption {
  id: number
  name: string
}

/** Строка настройки: один чат + роль, с которой в него добавляют участника */
export interface ChatRow {
  key: number
  /** id уже сохранённой настройки на backend, если она есть */
  id?: number
  chatId: number | null
  role: ChatRole
  chat?: ChatOption
}

export const CHAT_ROLE_OPTIONS = [
  { value: 1, label: 'Участник' },
  { value: 2, label: 'Администратор' },
  { value: 3, label: 'Читатель' },
]

/** Роль новой строки: сотрудник по умолчанию администратор, остальные — участники */
export const DEFAULT_CHAT_ROLE: Record<ChatAudience, ChatRole> = {
  student: 1,
  parent: 1,
  staff: 2,
}

export const normalizeChatRole = (role: unknown): ChatRole => {
  const normalizedRole = Number(role)

  return normalizedRole === 2 || normalizedRole === 3 ? normalizedRole : 1
}

let rowKeySequence = 0

/** Ключ строки нужен React: по индексу состояние съезжает при удалении строки */
export const createChatRow = (
  audience: ChatAudience,
  patch: Partial<ChatRow> = {},
): ChatRow => {
  rowKeySequence += 1

  return {
    key: rowKeySequence,
    chatId: null,
    role: DEFAULT_CHAT_ROLE[audience],
    ...patch,
  }
}

interface ChatAudienceRowsProps {
  rows: ChatRow[]
  audience: ChatAudience
  chatLabel: string
  onChange: (rows: ChatRow[]) => void
}

/**
 * Список строк «чат + роль» одной аудитории.
 * Общий блок настроек чатов для классов и для групп.
 */
const ChatAudienceRows: FC<ChatAudienceRowsProps> = ({
  rows,
  audience,
  chatLabel,
  onChange,
}) => {
  const chatsParams = useMemo(() => ({ type: 'group' }), [])

  const {
    data: chats = [],
    loading: loadingChats,
    searchQuery,
    setSearchQuery,
  } = useApiSearch<ChatOption[]>({
    apiMethod: chatApi.getUsersChatsList,
    searchKey: 'search',
    minQueryLength: 1,
    params: chatsParams,
  } as any)

  // Выбранные чаты держим в списке всегда: иначе после поиска пропадёт их название
  const options = useMemo(() => {
    const optionsMap = new Map<number, { label: string; value: number }>()

    rows.forEach((row) => {
      if (row.chatId && !optionsMap.has(row.chatId)) {
        optionsMap.set(row.chatId, {
          label: row.chat?.name || `Чат #${row.chatId}`,
          value: row.chatId,
        })
      }
    })
    ;(Array.isArray(chats) ? chats : []).forEach((chat) => {
      const chatId = Number(chat?.id)

      if (chatId && !optionsMap.has(chatId)) {
        optionsMap.set(chatId, {
          label: chat?.name || `Чат #${chatId}`,
          value: chatId,
        })
      }
    })

    return Array.from(optionsMap.values())
  }, [rows, chats])

  const usedChatIds = useMemo(
    () => rows.map((row) => row.chatId).filter(Boolean) as number[],
    [rows],
  )

  const notFoundContent =
    searchQuery.length === 0
      ? 'Начните вводить название чата'
      : 'Чаты не найдены'

  const updateRow = (key: number, patch: Partial<ChatRow>) => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  }

  return (
    <div className={classes.rows}>
      {rows.map((row, index) => (
        <div className={classes.row} key={row.key}>
          <div>
            {index === 0 && <p className={classes.label}>{chatLabel}</p>}
            <TooltipWrapper title={notFoundContent}>
              <div>
                <Select
                  wrapperClassName={classes.selectWrapper}
                  className={classes.select}
                  fields={{
                    loading: loadingChats,
                    showSearch: true,
                    allowClear: true,
                    filterSort: compareOptionsByLabel,
                    filterOption: false,
                    placeholder: 'Выберите чат',
                    notFoundContent,
                    onSearch: setSearchQuery,
                    value: row.chatId ?? undefined,
                    // Один и тот же чат нельзя настроить дважды для одной аудитории
                    options: options.map((option) => ({
                      ...option,
                      disabled:
                        option.value !== row.chatId &&
                        usedChatIds.includes(option.value),
                    })),
                    onChange: (value: number | null) => {
                      const chatId = value ? Number(value) : null
                      const option = options.find(
                        (item) => item.value === chatId,
                      )

                      updateRow(row.key, {
                        chatId,
                        chat: chatId
                          ? { id: chatId, name: option?.label ?? `Чат #${chatId}` }
                          : undefined,
                      })
                    },
                  }}
                />
              </div>
            </TooltipWrapper>
          </div>

          <div>
            {index === 0 && <p className={classes.label}>Роль в чате</p>}
            <Select
              hideSearchIcon
              wrapperClassName={classes.selectWrapper}
              className={classes.select}
              fields={{
                value: row.role,
                options: CHAT_ROLE_OPTIONS,
                onChange: (value: number) =>
                  updateRow(row.key, { role: normalizeChatRole(value) }),
              }}
            />
          </div>

          <button
            type="button"
            className={classes.remove}
            title="Удалить чат"
            onClick={() => onChange(rows.filter((item) => item.key !== row.key))}
          >
            <DeleteOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </button>
        </div>
      ))}

      <button
        type="button"
        className={classes.add}
        onClick={() => onChange([...rows, createChatRow(audience)])}
      >
        + Добавить чат
      </button>
    </div>
  )
}

export default ChatAudienceRows
